import { Suspense, lazy, useEffect } from 'react';
import {
  Outlet,
  createRootRouteWithContext,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  accountQuery,
  ApiError,
  getAccount,
  isLoggedIn,
  useAuthSession,
} from '@/api';
import { ChatHost } from '@/chat';
import {
  isRequireLoginScreenEnabled,
  shouldForceRequireLoginScreen,
} from '@/domain';
import { LiveFeedProvider } from '@/live/LiveFeedProvider';
import type { RouterContext } from '@/router-context';
import { AppShell, SportsRightRail } from '@/shell';
import { Button, EmptyState, Spinner } from '@/ui';

// Devtools are development-only and must never reach the production bundle.
// Set VITE_ENABLE_DEVTOOLS=false in .env.local to hide them while checking UI.
const Devtools =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEVTOOLS !== 'false'
    ? lazy(async () => {
        const [{ TanStackRouterDevtools }, { ReactQueryDevtools }] = await Promise.all([
          import('@tanstack/react-router-devtools'),
          import('@tanstack/react-query-devtools'),
        ]);
        return {
          default: () => (
            <>
              <TanStackRouterDevtools position="bottom-right" />
              <ReactQueryDevtools buttonPosition="bottom-left" />
            </>
          ),
        };
      })
    : () => null;

export const Route = createRootRouteWithContext<RouterContext>()({
  // Brand config must be known before any route renders; also gate RequireLoginScreen here
  // so anonymous visitors never reach the main app shell.
  beforeLoad: async ({ context, location }) => {
    await context.queryClient.ensureQueryData(accountQuery());
    if (
      shouldForceRequireLoginScreen({
        account: getAccount(),
        loggedIn: isLoggedIn(),
        pathname: location.pathname,
      })
    ) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  },
  // Idempotent with beforeLoad — keeps pending UI while brand config loads.
  loader: ({ context }) => context.queryClient.ensureQueryData(accountQuery()),
  component: RootLayout,
  pendingComponent: RoutePending,
  notFoundComponent: NotFound,
  errorComponent: RouteError,
});

function RootLayout() {
  const navigate = useNavigate();
  const { data: account } = useQuery(accountQuery());
  const { loggedIn } = useAuthSession();
  const { pathname, href, isSports, isLive, isCasino, isBetslip, isProfile, isPromotions } =
    useRouterState({
      select: (state) => {
        const path = state.location.pathname;
        return {
          pathname: path,
          href: state.location.href,
          isSports: path.startsWith('/sports'),
          isLive: path.startsWith('/live') && !path.startsWith('/livecasino'),
          isCasino: path.startsWith('/casino') || path.startsWith('/livecasino'),
          isBetslip: path === '/betslip' || path.startsWith('/betslip/'),
          isProfile: path.startsWith('/profile'),
          isPromotions: path.startsWith('/promotions'),
        };
      },
    });

  const loginOnly =
    isRequireLoginScreenEnabled(account) && !loggedIn;

  // beforeLoad only runs on navigation; cover logout / clearSession while staying on a route.
  useEffect(() => {
    if (
      !shouldForceRequireLoginScreen({
        account,
        loggedIn,
        pathname,
      })
    ) {
      return;
    }
    void navigate({
      to: '/login',
      search: { redirect: href },
      replace: true,
    });
  }, [account, href, loggedIn, navigate, pathname]);

  if (loginOnly) {
    return (
      <>
        <ChatHost />
        <Outlet />
        <Suspense fallback={null}>
          <Devtools />
        </Suspense>
      </>
    );
  }

  const shell = (
    <AppShell
      aside={isSports || isLive ? <SportsRightRail /> : undefined}
      hideSideNav={isLive || isProfile || isCasino || isPromotions}
      hideFooter={isBetslip}
      flushMain={isProfile}
    >
      <Outlet />
      <Suspense fallback={null}>
        <Devtools />
      </Suspense>
    </AppShell>
  );

  // Provider wraps the aside + outlet so the betslip rail (and mobile /betslip) share patches.
  const tree = isSports || isLive || isBetslip ? (
    <LiveFeedProvider>{shell}</LiveFeedProvider>
  ) : (
    shell
  );

  return (
    <>
      <ChatHost />
      {tree}
    </>
  );
}

function NotFound() {
  return (
    <EmptyState
      icon="danger"
      title="Page not found"
      description="The link may be out of date, or the page has moved."
      action={
        <Button
          onClick={() => {
            window.location.href = '/';
          }}
        >
          Back to home
        </Button>
      }
    />
  );
}

function RouteError({ error }: { error: Error }) {
  const description =
    error instanceof ApiError
      ? error.message
      : import.meta.env.DEV
        ? error.message
        : 'Something went wrong. Please try again.';

  return (
    <EmptyState
      icon="warning"
      title="Something went wrong"
      description={description}
      action={
        <Button variant="secondary" onClick={() => window.location.reload()}>
          Try again
        </Button>
      }
    />
  );
}

/** Exported so route-level pending states can share the shell's spinner. */
export function RoutePending() {
  return (
    <div className="flex justify-center py-16">
      <Spinner size="lg" label="Loading page" />
    </div>
  );
}
